import { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@/store/authStore';
import { X, Clock, MessageSquare, Activity, User, Calendar, Flag, Trash2, Send, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  low: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  medium: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  high: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  urgent: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const formatTime = (seconds) => {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatActivityAction = (action, oldValue, newValue, userName) => {
  switch (action) {
    case 'status_change':
      return `${userName} changed status from "${oldValue}" to "${newValue}"`;
    case 'priority_change':
      return `${userName} changed priority from "${oldValue}" to "${newValue}"`;
    case 'assignment':
      return `${userName} assigned a user to this task`;
    case 'time_start':
      return `${userName} started timer`;
    case 'time_stop':
      return `${userName} stopped timer (${formatTime(parseInt(newValue || 0))})`;
    case 'comment':
      return `${userName} added a comment`;
    case 'created':
      return `${userName} created this task`;
    default:
      return `${userName} performed ${action}`;
  }
};

// Render comment content with styled @mentions
const renderCommentWithMentions = (content) => {
  if (!content) return null;

  // Split by mention pattern @[name](id) or @[name](user:id)
  const parts = content.split(/(@\[[^\]]+\]\((?:user:)?[^)]+\))/g);

  return parts.map((part, index) => {
    const mentionMatch = part.match(/@\[([^\]]+)\]\((?:user:)?([^)]+)\)/);
    if (mentionMatch) {
      const [, name] = mentionMatch;
      return (
        <span key={index} className="text-cyan-400 font-medium cursor-pointer hover:text-cyan-300">
          @{name}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export default function TaskDetailDrawer({ task, projectMembers, statuses, onClose }) {
  const { user } = useAuthStore();
  const { updateTask, deleteTask, fetchComments, createComment, fetchActivityLogs, fetchTaskTimeLogs, startTimer, activeTimer } = useAppStore();

  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [priority, setPriority] = useState(task?.priority || 'medium');
  const [status, setStatus] = useState(task?.status || 'backlog');
  const [dueDate, setDueDate] = useState(task?.due_date || '');
  const [assignedUsers, setAssignedUsers] = useState(task?.assigned_users || []);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(-1);
  const [mentionedUsers, setMentionedUsers] = useState([]); // Track mentioned users: [{name: string, id: string}]

  const loadData = useCallback(async () => {
    if (!task?.id) return;
    setIsLoading(true);
    try {
      const [commentsData, activityData, timeData] = await Promise.all([
        fetchComments(task.id),
        fetchActivityLogs(task.id),
        fetchTaskTimeLogs(task.id),
      ]);
      setComments(commentsData);
      setActivityLogs(activityData);
      setTimeLogs(timeData);
    } catch (err) {
      console.error('Failed to load task data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [task?.id, fetchComments, fetchActivityLogs, fetchTaskTimeLogs]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setPriority(task.priority || 'medium');
      setStatus(task.status || 'backlog');
      setDueDate(task.due_date || '');
      setAssignedUsers(task.assigned_users || []);
    }
  }, [task]);

  const handleStatusChange = async (v) => {
    if (isEditing) {
      setStatus(v);
    } else {
      try {
        await updateTask(task.id, { status: v });
        toast.success('Status updated');
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const handlePriorityChange = async (v) => {
    if (isEditing) {
      setPriority(v);
    } else {
      try {
        await updateTask(task.id, { priority: v });
        toast.success('Priority updated');
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  // Handle Esc key to close drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSave = async () => {
    try {
      await updateTask(task.id, {
        title,
        description,
        priority,
        status,
        due_date: dueDate || null,
        assigned_users: assignedUsers,
      });
      setIsEditing(false);
      toast.success('Task updated');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await deleteTask(task.id);
      toast.success('Task deleted');
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleStartTimer = async () => {
    if (activeTimer) {
      toast.error('Stop current timer first');
      return;
    }
    try {
      await startTimer(task.id);
      toast.success('Timer started');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleTestEmail = async () => {
    try {
      await axios.post('http://localhost:8000/api/debug/test-email', { task_id: task.id }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Test email sent');
    } catch (err) {
      toast.error('Failed to send test email');
    }
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setNewComment(value);

    // Check for @mention
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      const spaceIndex = textAfterAt.indexOf(' ');
      if (spaceIndex === -1 && textAfterAt.length > 0) {
        setMentionSearch(textAfterAt.toLowerCase());
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
    setMentionSearch('');
  };

  const insertMention = (member) => {
    const lastAtIndex = newComment.lastIndexOf('@');
    const beforeMention = newComment.slice(0, lastAtIndex);
    // Insert clean @Name format (user-friendly)
    const mentionText = `@${member.user.name} `;
    setNewComment(beforeMention + mentionText);

    // Track this mention for later conversion
    setMentionedUsers(prev => [...prev, { name: member.user.name, id: member.user_id }]);

    setShowMentions(false);
    setMentionSearch('');
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    // Convert @Name mentions to @[Name](id) format before submitting
    let commentToSend = newComment;
    mentionedUsers.forEach(user => {
      // Replace @Name with @[Name](id), but only if not already in bracket format
      const regex = new RegExp(`@${user.name}(?!\\])`, 'g');
      commentToSend = commentToSend.replace(regex, `@[${user.name}](${user.id})`);
    });

    try {
      await createComment(task.id, commentToSend);
      setNewComment('');
      setMentionedUsers([]);
      toast.success('Comment added');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleAssignee = (userId) => {
    setAssignedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const filteredMembers = projectMembers?.filter((m) =>
    m.user?.name?.toLowerCase().includes(mentionSearch)
  ) || [];

  if (!task) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="task-detail-drawer">
      <div className="absolute inset-0 bg-black/50 animate-in fade-in duration-300" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-zinc-900 border-l border-zinc-800 h-full overflow-hidden flex flex-col animate-in slide-in-from-right duration-300 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={PRIORITY_COLORS[task.priority]}>
              <Flag className="w-3 h-3 mr-1" />
              {task.priority}
            </Badge>
            {task.total_time_seconds > 0 && (
              <span className="text-sm text-cyan-400 font-['JetBrains_Mono'] flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(task.total_time_seconds)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleTestEmail} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
              <Mail className="w-4 h-4 mr-1" /> Test Email
            </Button>
            <Button variant="ghost" size="sm" onClick={handleStartTimer} disabled={!!activeTimer} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
              <Clock className="w-4 h-4 mr-1" /> Start Timer
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            {/* Title & Description */}
            <div className="space-y-4">
              {isEditing ? (
                <>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="text-xl font-semibold bg-zinc-950 border-zinc-800"
                    placeholder="Task title"
                    data-testid="task-title-edit"
                  />
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                    placeholder="Task description..."
                    data-testid="task-desc-edit"
                  />
                </>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-white cursor-pointer hover:text-cyan-400" onClick={() => setIsEditing(true)}>
                    {task.title}
                  </h2>
                  <p className="text-zinc-400 cursor-pointer hover:text-zinc-300" onClick={() => setIsEditing(true)}>
                    {task.description || 'No description. Click to add.'}
                  </p>
                </>
              )}
            </div>

            {/* Properties */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1.5">Status</label>
                <Select value={isEditing ? status : task.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="task-status-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {statuses?.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <span>{s.title}</span>
                          {s.is_in_progress && (
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shadow-[0_0_5px_rgba(6,182,212,0.5)]" />
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1.5">Priority</label>
                <Select value={isEditing ? priority : task.priority} onValueChange={handlePriorityChange}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="task-priority-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1.5">Due Date</label>
                <Input
                  type="date"
                  value={isEditing ? dueDate : (task.due_date || '')}
                  onChange={isEditing ? (e) => setDueDate(e.target.value) : async (e) => {
                    await updateTask(task.id, { due_date: e.target.value || null });
                    toast.success('Due date updated');
                  }}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="task-due-date-edit"
                />
              </div>
            </div>

            {/* Assignees */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2">Assignees</label>
              <div className="flex flex-wrap gap-2">
                {projectMembers?.map((member) => {
                  const isAssigned = (isEditing ? assignedUsers : task.assigned_users)?.includes(member.user_id);
                  return (
                    <button
                      key={member.user_id}
                      onClick={() => {
                        if (isEditing) {
                          toggleAssignee(member.user_id);
                        } else {
                          const newAssignees = isAssigned
                            ? task.assigned_users.filter((id) => id !== member.user_id)
                            : [...(task.assigned_users || []), member.user_id];
                          updateTask(task.id, { assigned_users: newAssignees }).then(() => {
                            toast.success(isAssigned ? 'Unassigned' : 'Assigned');
                            loadData();
                          });
                        }
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${isAssigned
                        ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                        }`}
                      data-testid={`assignee-${member.user_id}`}
                    >
                      <Avatar className="w-5 h-5">
                        <AvatarFallback className="text-xs bg-zinc-700">
                          {member.user?.name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{member.user?.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {isEditing && (
              <div className="flex gap-2">
                <Button onClick={handleSave} className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="save-task-btn">
                  Save Changes
                </Button>
                <Button variant="ghost" onClick={() => setIsEditing(false)}>Cancel</Button>
              </div>
            )}

            {/* Tabs: Comments, Activity, Time Logs */}
            <Tabs defaultValue="comments" className="mt-6">
              <TabsList className="bg-zinc-800 border border-zinc-700">
                <TabsTrigger value="comments" className="data-[state=active]:bg-zinc-700">
                  <MessageSquare className="w-4 h-4 mr-1" /> Comments ({comments.length})
                </TabsTrigger>
                <TabsTrigger value="activity" className="data-[state=active]:bg-zinc-700">
                  <Activity className="w-4 h-4 mr-1" /> Activity ({activityLogs.length})
                </TabsTrigger>
                <TabsTrigger value="time" className="data-[state=active]:bg-zinc-700">
                  <Clock className="w-4 h-4 mr-1" /> Time ({timeLogs.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="comments" className="mt-4 space-y-4">
                {/* Comment Input */}
                <div className="space-y-2">
                  <div className="relative">
                    <Textarea
                      value={newComment}
                      onChange={handleCommentChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.shiftKey) {
                          e.preventDefault();
                          handleSubmitComment();
                        }
                      }}
                      placeholder="Write a comment... (Shift + Enter to post) Type @ to mention someone"
                      className="bg-zinc-950 border-zinc-800 pr-12 min-h-[80px]"
                      data-testid="comment-input"
                    />
                    <Button
                      size="icon"
                      onClick={handleSubmitComment}
                      disabled={!newComment.trim()}
                      className="absolute bottom-2 right-2 bg-cyan-500 text-black hover:bg-cyan-400 h-8 w-8 disabled:opacity-50"
                      data-testid="submit-comment-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    {showMentions && filteredMembers.length > 0 && (
                      <div className="absolute bottom-full left-0 w-full bg-zinc-900 border border-zinc-800 rounded-lg mb-1 max-h-40 overflow-y-auto z-10 shadow-xl">
                        <div className="px-3 py-1.5 text-xs text-zinc-500 border-b border-zinc-800">Mention someone</div>
                        {filteredMembers.map((member) => (
                          <button
                            key={member.user_id}
                            onClick={() => insertMention(member)}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-800 text-left"
                          >
                            <Avatar className="w-6 h-6">
                              <AvatarFallback className="text-xs bg-zinc-700">
                                {member.user?.name?.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-white">{member.user?.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Preview of mentions */}
                  {mentionedUsers.length > 0 && (
                    <div className="text-xs text-zinc-500 px-1">
                      Preview: {newComment.split(/(@\w+)/g).map((part, idx) => {
                        const mentionedUser = mentionedUsers.find(u => `@${u.name}` === part);
                        if (mentionedUser) {
                          return (
                            <span key={idx} className="text-cyan-400 font-medium">
                              {part}
                            </span>
                          );
                        }
                        return <span key={idx}>{part}</span>;
                      })}
                    </div>
                  )}
                </div>

                {/* Comments List */}
                {isLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-zinc-500 text-center py-4">No comments yet</p>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex gap-3" data-testid={`comment-${comment.id}`}>
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-zinc-700 text-xs">
                            {comment.user?.name?.charAt(0).toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">{comment.user?.name}</span>
                            <span className="text-xs text-zinc-500">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap">
                            {renderCommentWithMentions(comment.content)}
                          </p>
                        </div>
                        {comment.user_id === user?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-zinc-500 hover:text-red-400"
                            onClick={async () => {
                              await useAppStore.getState().deleteComment(comment.id);
                              loadData();
                              toast.success('Comment deleted');
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="activity" className="mt-4">
                {activityLogs.length === 0 ? (
                  <p className="text-zinc-500 text-center py-4">No activity yet</p>
                ) : (
                  <div className="space-y-3">
                    {activityLogs.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 text-sm" data-testid={`activity-${log.id}`}>
                        <div className="w-2 h-2 rounded-full bg-zinc-600 mt-2" />
                        <div>
                          <p className="text-zinc-300">
                            {formatActivityAction(log.action, log.old_value, log.new_value, log.user?.name || 'Someone')}
                          </p>
                          <p className="text-xs text-zinc-500">{formatDate(log.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="time" className="mt-4">
                {timeLogs.length === 0 ? (
                  <p className="text-zinc-500 text-center py-4">No time entries yet</p>
                ) : (
                  <div className="space-y-3">
                    {timeLogs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg" data-testid={`timelog-${log.id}`}>
                        <div>
                          <p className="text-sm text-white">{formatDate(log.start_time)}</p>
                          {log.end_time && (
                            <p className="text-xs text-zinc-500">to {formatDate(log.end_time)}</p>
                          )}
                        </div>
                        <span className="text-cyan-400 font-['JetBrains_Mono']">
                          {log.is_running ? 'Running...' : formatTime(log.duration_seconds)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 flex justify-between">
          <Button variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={handleDelete} data-testid="delete-task-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Delete Task
          </Button>
          <span className="text-xs text-zinc-500">
            Created {formatDate(task.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
}
